import { createSelector } from 'reselect';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { getFormValues } from 'redux-form';

import FORM_NAMES from 'constants/formNames';
import {
  ACTION_NAMES,
  ACTION_ATTRIBUTES_NAMES,
  ANNOUNCEMENT_TYPE,
} from 'scenes/NewFlow/constants';
import { convertMsecToSec } from 'store/utils/newFlow/normalisers';
import { conditionGroupToString } from 'store/utils/newFlow/conditionStringify';

export const getFlowState = ({ newFlow }) => newFlow;

export const getActionsState = createSelector(
  getFlowState,
  state => state.actions,
);

export const getActions = createSelector(
  getActionsState,
  actions => actions.list.data,
);

export const getActionsWithName = createSelector(getActions, actions =>
  actions.filter(action => get(action, 'name')),
);

export const getActionsIsLoading = createSelector(
  getActionsState,
  actions => actions.list.isLoading,
);

export const getIsUpdating = createSelector(
  getActionsState,
  actions => actions.update.isLoading,
);

export const getIsAdding = createSelector(
  getActionsState,
  actions => actions.add.isLoading,
);

export const getActionsData = createSelector(
  getActionsState,
  actions => actions.show.data,
);

export const getActionById = actionId =>
  createSelector(getActionsData, data => data[actionId] || {});

export const getActionNameById = actionId =>
  createSelector(getActionById(actionId), data => get(data, 'name', ''));

export const getActionAttributeFields = actionId =>
  createSelector(getActionById(actionId), action => get(action, 'fields', []));

export const getBackEndCallActionInputFields = actionId =>
  createSelector(getActionById(actionId), data =>
    get(data, 'custom_module_call.input_fields', []),
  );

export const getBackEndCallActionOutputFields = actionId =>
  createSelector(getActionById(actionId), data =>
    get(data, 'custom_module_call.output_fields', []),
  );

export const getActionType = actionId =>
  createSelector(getActionById(actionId), data => get(data, 'label', ''));

export const getActionIsLoading = createSelector(
  getActionsState,
  actions => actions.show.isLoading,
);

export const getActionInfo = createSelector(getActionsState, actions =>
  get(actions, 'info.data', {
    condition_group: null,
    prompt_list: null,
  }),
);

export const getActionInfoIsLoading = createSelector(
  getActionsState,
  actions => actions.info.isLoading,
);

export const getActionInfoAttributes = createSelector(getActionInfo, action =>
  pick(action, Object.values(ACTION_ATTRIBUTES_NAMES)),
);

export const getActionFormValues = createSelector(
  getFormValues(FORM_NAMES.NEW_FLOW.DRAWER),
  values =>
    values || {
      name: '',
      attributes: {},
      attributeFields: [],
    },
);

export const getActionInfoType = createSelector(getActionInfo, actionInfo =>
  get(actionInfo, 'label', ''),
);

export const getActionInfoPromptList = createSelector(
  getActionInfo,
  actionInfo => get(actionInfo, 'prompt_list'),
);

export const getActionPrompts = createSelector(
  getActionInfoPromptList,
  getActionInfoType,
  (promptList, type) => {
    if (type === ACTION_NAMES.BACKENDCALL) {
      return (
        promptList || {
          initial_wait_prompt: {
            utterances: [{ utterance_type: 'normal', parts: [{ text: '' }] }],
          },
          additional_wait_prompt: {
            utterances: [{ utterance_type: 'normal', parts: [{ text: '' }] }],
          },
        }
      );
    }

    return (
      promptList || {
        normal_prompt: {
          utterances: [{ utterance_type: 'normal', parts: [{ text: '' }] }],
        },
        error_prompt: {
          utterances: [{ utterance_type: 'normal', parts: [{ text: '' }] }],
        },
      }
    );
  },
);

export const getActionInfoConditionGroups = createSelector(
  getActionInfo,
  data => conditionGroupToString(get(data, 'condition_group', {})),
);

export const getActionInfoName = createSelector(getActionInfo, actionInfo =>
  get(actionInfo, 'name', ''),
);

export const getActionInfoAttributeFields = createSelector(
  getActionInfo,
  actionInfo => get(actionInfo, 'fields', []),
);

export const getAttributeFieldsNames = createSelector(
  getActionInfoAttributeFields,
  fields => fields.map(field => field.name),
);

export const getActionInfoCustomModuleCall = createSelector(
  getActionInfo,
  actionInfoData => get(actionInfoData, 'custom_module_call', {}),
);

export const getBackEndCallActionInfoInputFields = createSelector(
  getActionInfo,
  actionInfoData => get(actionInfoData, 'custom_module_call.input_fields', []),
);

export const getBackEndCallActionInfoOutputFields = createSelector(
  getActionInfo,
  actionInfoData => get(actionInfoData, 'custom_module_call.output_fields', []),
);

export const getActionInfoNormalisedCustomModuleCall = createSelector(
  getActionInfoCustomModuleCall,
  customModuleCall => ({
    ...customModuleCall,
    max_wait_time: convertMsecToSec(customModuleCall.max_wait_time),
    initial_wait_time: convertMsecToSec(customModuleCall.initial_wait_time),
    additional_wait_time: convertMsecToSec(
      customModuleCall.additional_wait_time,
    ),
    input_fields: get(customModuleCall, 'input_fields', []),
    output_fields: get(customModuleCall, 'output_fields', []),
  }),
);

export const getInputFieldsNames = createSelector(
  getBackEndCallActionInfoInputFields,
  inputFields => inputFields.map(field => field.name),
);

export const getOutputFieldsNames = createSelector(
  getBackEndCallActionInfoOutputFields,
  outputFields => outputFields.map(field => field.name),
);

export const getActionInfoAnnouncementType = createSelector(
  getActionInfo,
  actionInfo => get(actionInfo, 'announcement_type', ANNOUNCEMENT_TYPE.FIELD),
);
